<template>
  <Card class="w-full border-primary/20">
    <CardHeader class="flex flex-col gap-3">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div class="flex flex-col gap-1">
          <CardTitle class="text-base">
            <Badge variant="outline">
              v{{ checklistTemplateVersionLabel }}
            </Badge>
            <span class="ml-2">{{ checklistTemplateTitle }}</span>
          </CardTitle>
          <div class="flex flex-wrap items-center gap-2">
            <Badge variant="secondary">
              {{ checklistTemplateStatusLabel }}
            </Badge>
            <span v-if="draftOfChecklistTemplateId > 0" class="text-xs text-muted-foreground">
              Editing a new version of template #{{ draftOfChecklistTemplateId }}
            </span>
          </div>
          <CardDescription>
            {{ checklistTemplateDescriptionLabel }}
          </CardDescription>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <Button variant="outline" :disabled="isBusy || !canEditChecklistTemplateDetails" @click="openEditTemplateDetailsModal">
            Edit Name & Description
          </Button>
          <Button variant="outline" :disabled="isBusy || !canAbortChecklistTemplate" @click="abortChecklistTemplate">
            Abort
          </Button>
          <Button :loading="publishingChecklistTemplate" :disabled="isBusy || !canPublishChecklistTemplate" @click="publishChecklistTemplate">
            Publish
          </Button>
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>Template ID #{{ checklistTemplate.id }}</span>
        <span v-if="checklistTemplate.user?.name">Owner {{ checklistTemplate.user.name }}</span>
      </div>
    </CardHeader>

    <CardContent class="flex flex-col gap-4">
      <p v-if="actionErrorMessage" class="text-sm text-destructive">
        {{ actionErrorMessage }}
      </p>

      <ChecklistTemplateItems :checklist-template-id="checklistTemplate.id" :is-editable="true" />

      <div class="flex flex-col gap-2 border-t pt-4">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <p class="text-sm font-medium">
            Quick add checklist template item
          </p>
          <Button variant="outline" size="sm" :disabled="!canImportChecklistTemplateItems" @click="openChecklistTemplateImportModal">
            Import Items
          </Button>
        </div>
        <form class="flex flex-col gap-2" @submit.prevent="addChecklistTemplateItem">
          <Input :id="quickAddInputId" v-model="newItemInstruction" placeholder="Instruction" :disabled="isAddingChecklistTemplateItem || isBusy" />

          <p v-if="quickAddErrorMessage" class="text-xs text-destructive">
            {{ quickAddErrorMessage }}
          </p>

          <div class="flex justify-end">
            <Button type="submit" size="sm" :loading="isAddingChecklistTemplateItem" :disabled="!canAddChecklistTemplateItem && !isAddingChecklistTemplateItem">
              Add Item
            </Button>
          </div>
        </form>
      </div>
    </CardContent>
  </Card>

  <ChecklistTemplateEditDetailsModal
    ref="editChecklistTemplateDetailsModal"
    :checklist-template="checklistTemplate"
    @updated="handleChecklistTemplateDetailsUpdated"
  />
  <ChecklistTemplateImportModal
    ref="checklistTemplateImportModal"
    :checklist-template="checklistTemplate"
    @imported="handleChecklistTemplateItemsImported"
  />
</template>

<script setup>
import { computed, nextTick, ref } from 'vue'
import { Badge } from '@/components/ui/badge/index.js'
import { Button } from '@/components/ui/button/index.js'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card/index.js'
import { Input } from '@/components/ui/input/index.js'
import ChecklistTemplateEditDetailsModal from './ChecklistTemplateEditDetailsModal.vue'
import ChecklistTemplateImportModal from './ChecklistTemplateImportModal.vue'
import ChecklistTemplateItems from './ChecklistTemplateItems.vue'
import { useChecklistItemTemplatesStore } from '../../stores/checklistItemTemplatesStore.js'
import { useChecklistTemplatesStore } from '../../stores/checklistTemplatesStore.js'

const props = defineProps({
  checklistTemplate: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['updated', 'published', 'aborted'])

const checklistTemplatesStore = useChecklistTemplatesStore(`editor_${props.checklistTemplate.id}`)
const checklistItemTemplatesStore = useChecklistItemTemplatesStore(`checklist_template_${props.checklistTemplate.id}`)
const editChecklistTemplateDetailsModal = ref(null)
const checklistTemplateImportModal = ref(null)
const newItemInstruction = ref('')
const quickAddErrorMessage = ref(null)
const actionErrorMessage = ref(null)
const publishingChecklistTemplate = ref(false)
const abortingChecklistTemplate = ref(false)

const quickAddInputId = computed(() => `checklist-template-editor-quick-add-${props.checklistTemplate.id}`)
const draftOfChecklistTemplateId = computed(() => Number(props.checklistTemplate?.draft_of_id ?? 0))
const checklistTemplateTitle = computed(() => {
  return props.checklistTemplate.title ?? `Checklist template #${props.checklistTemplate.id}`
})
const checklistTemplateDescriptionLabel = computed(() => {
  const description = String(props.checklistTemplate.description ?? '').trim()

  return description.length > 0 ? description : null
})
const checklistTemplateVersionLabel = computed(() => {
  const normalizedVersion = Number(props.checklistTemplate?.version ?? 1)

  if (Number.isNaN(normalizedVersion)) {
    return '1.00'
  }

  return normalizedVersion.toFixed(2)
})
const checklistTemplateStatusLabel = computed(() => {
  const normalizedStatus = String(props.checklistTemplate?.status ?? 'draft')

  return normalizedStatus
    .split('_')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ')
})
const isDraftChecklistTemplate = computed(() => props.checklistTemplate?.status === 'draft')
const isBusy = computed(() => publishingChecklistTemplate.value || abortingChecklistTemplate.value)
const canEditChecklistTemplateDetails = computed(() => isDraftChecklistTemplate.value)
const canPublishChecklistTemplate = computed(() => isDraftChecklistTemplate.value)
const canAbortChecklistTemplate = computed(() => isDraftChecklistTemplate.value)
const canImportChecklistTemplateItems = computed(() => isDraftChecklistTemplate.value && !isBusy.value)
const isAddingChecklistTemplateItem = computed(() => {
  return checklistItemTemplatesStore.isAddingChecklistTemplateItem(props.checklistTemplate.id)
})
const canAddChecklistTemplateItem = computed(() => {
  return isDraftChecklistTemplate.value && newItemInstruction.value.trim().length > 0 && !isBusy.value
})

const focusQuickAddInput = async () => {
  await nextTick()

  const nativeInput = document.getElementById(quickAddInputId.value)

  if (!nativeInput) {
    return
  }

  nativeInput.focus()
}

const addChecklistTemplateItem = async () => {
  if (!canAddChecklistTemplateItem.value) {
    return
  }

  quickAddErrorMessage.value = null

  try {
    await checklistItemTemplatesStore.createItemForChecklistTemplate({
      checklistTemplateId: props.checklistTemplate.id,
      instruction: newItemInstruction.value,
    })

    newItemInstruction.value = ''
    await focusQuickAddInput()
  } catch (error) {
    quickAddErrorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to add checklist template item.'
  }
}

const openEditTemplateDetailsModal = () => {
  if (!canEditChecklistTemplateDetails.value || isBusy.value) {
    return
  }

  editChecklistTemplateDetailsModal.value?.openModal()
}

const openChecklistTemplateImportModal = () => {
  if (!canImportChecklistTemplateItems.value) {
    return
  }

  checklistTemplateImportModal.value?.openModal()
}

const handleChecklistTemplateDetailsUpdated = (updatedChecklistTemplate) => {
  if (!updatedChecklistTemplate) {
    return
  }

  actionErrorMessage.value = null
  emit('updated', updatedChecklistTemplate)
}

const handleChecklistTemplateItemsImported = async (updatedChecklistTemplate) => {
  if (!updatedChecklistTemplate) {
    return
  }

  actionErrorMessage.value = null

  try {
    await checklistItemTemplatesStore.fetchItemsForChecklistTemplate(props.checklistTemplate.id)
    emit('updated', updatedChecklistTemplate)
  } catch (error) {
    actionErrorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to refresh checklist template items.'
  }
}

const publishChecklistTemplate = async () => {
  if (!canPublishChecklistTemplate.value || isBusy.value) {
    return
  }

  publishingChecklistTemplate.value = true
  actionErrorMessage.value = null

  try {
    const publishedChecklistTemplate = await checklistTemplatesStore.publishChecklistTemplate(props.checklistTemplate.id)
    emit('published', publishedChecklistTemplate)
  } catch (error) {
    actionErrorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to publish checklist template.'
  } finally {
    publishingChecklistTemplate.value = false
  }
}

const abortChecklistTemplate = async () => {
  if (!canAbortChecklistTemplate.value || isBusy.value) {
    return
  }

  if (!window.confirm('Abort this draft and discard its unpublished changes?')) {
    return
  }

  abortingChecklistTemplate.value = true
  actionErrorMessage.value = null

  try {
    const abortPayload = await checklistTemplatesStore.abortChecklistTemplate(props.checklistTemplate.id)
    emit('aborted', abortPayload)
  } catch (error) {
    actionErrorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to abort checklist template draft.'
  } finally {
    abortingChecklistTemplate.value = false
  }
}
</script>
